import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Search,
  Download,
  Loader2,
  Table2,
  RefreshCw,
  File,
  X,
  AlertCircle,
  BarChart3,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Hash,
  DollarSign,
  ChevronsUpDown,
  ArrowUp,
  ArrowDown,
  List,
  Eye,
  Layers,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { periodDisplayName, type TrialBalance, type TrialBalanceUpload, type Entity, type Period, type TrialBalanceTranslationResponse, type TranslatedTrialBalanceRow } from '@/types';
import { isIcpCode } from '@/utils/icp';
import { trialBalancesApi, entitiesApi, periodsApi, exportsApi, aiSuggestionsApi } from '@/api/client';
import SearchableSelect from '@/components/SearchableSelect';
import { useAppStore, useAuthStore } from '@/store';

// --- Format Currency ---------------------------------------------------------

function formatCurrency(amount: number): string {
  if (amount === 0) return '\u2014';
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// --- CTA Synthetic Row Builder -----------------------------------------------

const CTA_ACCOUNT_CODE = '999999';
const CTA_ACCOUNT_NAME = 'Currency Translation Adjustment (CTA)';

function buildCtaSyntheticRow(_ctaAmount: number, currency: string): TrialBalance {
  return {
    id: 'cta-synthetic',
    entity_id: '',
    period_id: '',
    account_id: 0,
    debit: 0,
    credit: 0,
    local_currency_debit: 0,
    local_currency_credit: 0,
    currency,
    source: 'system',
    status: 'validated',
    created_at: '',
    updated_at: '',
    account_code: CTA_ACCOUNT_CODE,
    account_name: CTA_ACCOUNT_NAME,
    icp_code: '',
  };
}

function buildCtaTranslatedRow(ctaAmount: number, fromCurrency: string, closingRate: number | null): TranslatedTrialBalanceRow {
  return {
    account_code: CTA_ACCOUNT_CODE,
    account_name: CTA_ACCOUNT_NAME,
    account_type: 'expense',
    from_currency: fromCurrency,
    local_debit: 0,
    local_credit: 0,
    translated_debit: ctaAmount < 0 ? Math.abs(ctaAmount) : 0,
    translated_credit: ctaAmount > 0 ? ctaAmount : 0,
    rate: closingRate ?? 0,
    rate_type: 'closing',
  };
}

// --- Statement Table Component -----------------------------------------------

type StatementItem = {
  code: string; name: string; fs_line_item: string; balance: number;
  parent_code?: string | null; parent_name?: string | null;
  translated_balance?: number | null;
  fx_rate?: number;
  rate_type?: string;
};
type SubsectionDef = { title: string; items: StatementItem[]; total: number; totalLabel: string; translatedTotal?: number; fx_rate?: number; rate_type?: string };
type SectionDef = { title: string; subsections: SubsectionDef[]; grandTotal: number | null; grandTotalLabel: string; translatedGrandTotal?: number | null; fx_rate?: number; rate_type?: string };

type StatementLevel = 1 | 2 | 3 | 'all';

// --- Level Filtering ---------------------------------------------------------

function getFirstRate(items: StatementItem[]) {
  const item = items.find(i => i.fx_rate !== undefined);
  return { fx_rate: item?.fx_rate, rate_type: item?.rate_type };
}


function collapseToParentGroups(items: StatementItem[]): StatementItem[] {
  const parentMap = new Map<string, { name: string; total: number; trTotal: number; fxRule?: number; rateTypeStr?: string }>();
  const result: StatementItem[] = [];

  for (const item of items) {
    if (item.parent_code) {
      if (!parentMap.has(item.parent_code)) {
        parentMap.set(item.parent_code, {
          name: item.parent_name ?? item.parent_code,
          total: 0,
          trTotal: 0,
          fxRule: item.fx_rate,
          rateTypeStr: item.rate_type
        });
      }
      const g = parentMap.get(item.parent_code)!;
      g.total += item.balance;
      g.trTotal += item.translated_balance ?? 0;
      // Inherit the first encountered fx rate
      if (g.fxRule === undefined && item.fx_rate !== undefined) {
        g.fxRule = item.fx_rate;
        g.rateTypeStr = item.rate_type;
      }
    } else {
      result.push(item);
    }
  }

  for (const [parentCode, group] of parentMap) {
    const collapsedItem: StatementItem = {
      code: parentCode,
      name: group.name,
      fs_line_item: '',
      balance: group.total,
      parent_code: null,
      parent_name: null,
    };
    if (group.trTotal !== 0) {
      collapsedItem.translated_balance = group.trTotal;
    }
    if (group.fxRule !== undefined) {
      collapsedItem.fx_rate = group.fxRule;
      collapsedItem.rate_type = group.rateTypeStr;
    }
    result.push(collapsedItem);
  }

  return result;
}

/** Filter/aggregate sections based on the selected drill-down level. */
function filterSectionsByLevel(sections: SectionDef[], level: StatementLevel): SectionDef[] {
  if (level === 'all') return sections;

  if (level === 1) {
    // Only grand totals per section — no subsections
    return sections.map((s) => ({ ...s, subsections: [] }));
  }

  if (level === 2) {
    // Subsection totals — no individual items
    return sections.map((s) => ({
      ...s,
      subsections: s.subsections.map((sub) => ({ ...sub, items: [] })),
    }));
  }

  if (level === 3) {
    // Parent-group totals — collapse children into parent summaries
    return sections.map((s) => ({
      ...s,
      subsections: s.subsections.map((sub) => ({ ...sub, items: collapseToParentGroups(sub.items) })),
    }));
  }

  return sections;
}

/** Render items grouped by parent_code. Items with no parent render at normal indent;
 *  items sharing a parent render under a parent header row with extra indent + optional subtotal. */
function renderGroupedItems(items: StatementItem[], showTr: boolean = false) {
  type ParentGroup = { name: string; items: StatementItem[]; subtotal: number; trSubtotal: number; fxRule?: number | null; rateTypeStr?: string | null };
  const parentMap = new Map<string, ParentGroup>();
  const direct: StatementItem[] = [];

  for (const item of items) {
    if (item.parent_code) {
      if (!parentMap.has(item.parent_code)) {
        parentMap.set(item.parent_code, { name: item.parent_name ?? item.parent_code, items: [], subtotal: 0, trSubtotal: 0 });
      }
      const g = parentMap.get(item.parent_code)!;
      g.items.push(item);
      g.subtotal += item.balance;
      g.trSubtotal += item.translated_balance ?? 0;
    } else {
      direct.push(item);
    }
  }

  const colCount = showTr ? 6 : 3;

  return (
    <>
      {/* Items without a parent group (top-level) */}
      {direct.map((item, idx) => (
        <tr key={`direct-${item.code}-${idx}`} className="hover:bg-gray-50/50">
          <td className="py-1 pl-4 pr-2 text-gray-700">
            {item.code ? <><span className="font-mono text-xs text-gray-400 mr-2">{item.code}</span>{item.name}</> : item.name}
          </td>
          <td className="py-1 px-2 text-center text-gray-400 text-xs">—</td>
          <td className="py-1 pl-4 text-right font-mono text-gray-800">
            {item.balance !== 0 ? formatCurrency(item.balance) : <span className="text-gray-300">—</span>}
          </td>
          {showTr && (
            <>
              <td className="py-1 pl-4 text-right font-mono text-indigo-700">
                {item.translated_balance != null && item.translated_balance !== 0
                  ? formatCurrency(item.translated_balance)
                  : <span className="text-gray-300">—</span>}
              </td>
              <td className="py-1 px-4 text-right font-mono text-gray-700 text-xs">
                {item.fx_rate != null ? item.fx_rate.toFixed(6) : <span className="text-gray-300">—</span>}
              </td>
              <td className="py-1 pr-4 text-right">
                {item.rate_type ? (
                  <span className={clsx(
                    'text-[10px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide',
                    item.rate_type.toLowerCase() === 'average'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-100 text-gray-600'
                  )}>
                    {item.rate_type === 'Average' ? 'Avg' : 'Close'}
                  </span>
                ) : <span className="text-gray-300">—</span>}
              </td>
            </>
          )}
        </tr>
      ))}
      {/* Items grouped under a parent header */}
      {Array.from(parentMap.entries()).map(([parentCode, group], pIdx) => (
        <React.Fragment key={`pg-${parentCode}-${pIdx}`}>
          {/* Parent header row */}
          <tr className="bg-blue-50/30 border-t border-gray-100">
            <td colSpan={colCount} className="py-1.5 pl-4 text-xs font-semibold text-gray-700">
              <span className="font-mono text-gray-400 mr-2">{parentCode}</span>{group.name}
            </td>
          </tr>
          {/* Child items — indented */}
          {group.items.map((item, idx) => (
            <tr key={`child-${parentCode}-${item.code}-${idx}`} className="hover:bg-gray-50/50">
              <td className="py-1 pl-10 pr-2 text-gray-600">
                <span className="font-mono text-xs text-gray-400 mr-2">{item.code}</span>{item.name}
              </td>
              <td className="py-1 px-2 text-center text-gray-400 text-xs">—</td>
              <td className="py-1 pl-4 text-right font-mono text-gray-700">
                {item.balance !== 0 ? formatCurrency(item.balance) : <span className="text-gray-300">—</span>}
              </td>
              {showTr && (
                <>
                  <td className="py-1 pl-4 text-right font-mono text-indigo-600">
                    {item.translated_balance != null && item.translated_balance !== 0
                      ? formatCurrency(item.translated_balance)
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-1 px-4 text-right font-mono text-gray-700 text-xs">
                    {item.fx_rate != null ? item.fx_rate.toFixed(6) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-1 pr-4 text-right">
                    {item.rate_type ? (
                      <span className={clsx(
                        'text-[10px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide',
                        item.rate_type.toLowerCase() === 'average'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      )}>
                        {item.rate_type === 'Average' ? 'Avg' : 'Close'}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                </>
              )}
            </tr>
          ))}
          {/* Parent subtotal — only when there are multiple children */}
          {group.items.length > 1 && (
            <tr className="border-t border-gray-100 bg-gray-50/50">
              <td className="py-1 pl-10 pr-2 text-gray-600 text-xs font-semibold italic">Total {group.name}</td>
              <td />
              <td className="py-1 pl-4 text-right font-mono text-gray-700 text-xs font-semibold">
                {formatCurrency(group.subtotal)}
              </td>
              {showTr && (
                <>
                  <td className="py-1 pl-4 text-right font-mono text-indigo-600 text-xs font-semibold">
                    {formatCurrency(group.trSubtotal)}
                  </td>
                  <td className="py-1 px-4 text-right font-mono text-gray-700 text-xs">
                    {group.fxRule != null ? group.fxRule.toFixed(6) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="py-1 pr-4 text-right">
                    {group.rateTypeStr ? (
                      <span className={clsx(
                        'text-[10px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide',
                        group.rateTypeStr.toLowerCase() === 'average'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      )}>
                        {group.rateTypeStr === 'Average' ? 'Avg' : 'Close'}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                </>
              )}
            </tr>
          )}
        </React.Fragment>
      ))}
    </>
  );
}

function StatementTable({ currency, sections, translatedCurrency }: { currency: string; sections: SectionDef[]; translatedCurrency?: string }) {
  const showTr = Boolean(translatedCurrency);
  const colCount = showTr ? 6 : 3;
  return (
    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
      <thead className="sticky top-0 z-20 shadow-[inset_0_-1px_0_0_#e5e7eb]">
        <tr className="border-b border-gray-200 bg-white">
          <th className="text-left py-1.5 pr-4 font-semibold text-gray-700 w-[45%] bg-white">Description</th>
          <th className="text-center py-1.5 px-2 font-semibold text-gray-700 w-16 bg-white">Notes</th>
          <th className="text-right py-1.5 pl-4 font-semibold text-gray-700 w-36 bg-white">
            {showTr ? <span className="text-gray-500 text-xs font-medium">{currency} (Base)</span> : currency}
          </th>
          {showTr && (
            <>
              <th className="text-right py-1.5 pl-4 font-semibold text-indigo-700 w-36 bg-white">
                <span className="text-indigo-600 text-xs font-medium">{translatedCurrency} (Translated)</span>
              </th>
              <th className="text-right py-1.5 px-4 font-semibold text-gray-700 w-24 bg-white">FX Rate</th>
              <th className="text-right py-1.5 pr-4 font-semibold text-gray-700 w-24 bg-white">Rate Type</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {sections.map((section, sIdx) => (
          <React.Fragment key={`section-${section.title}-${sIdx}`}>
            {/* Major section header */}
            <tr className="bg-gray-50">
              <td colSpan={colCount} className="py-2 px-1 font-bold text-gray-800 text-sm uppercase tracking-wide">
                {section.title}
              </td>
            </tr>
            {section.subsections.map((sub, subIdx) => (
              <React.Fragment key={`sub-${section.title}-${sub.totalLabel}-${subIdx}`}>
                {sub.title && (
                  <tr>
                    <td colSpan={colCount} className="py-1.5 px-2 text-gray-500 italic text-xs font-medium">
                      {sub.title}
                    </td>
                  </tr>
                )}
                {/* Render items grouped by parent */}
                {renderGroupedItems(sub.items, showTr)}
                {/* Subtotal row */}
                <tr className="border-t border-gray-100">
                  <td className="py-1.5 pl-2 pr-2 font-semibold text-gray-700 text-xs" style={{ backgroundColor: '#D6E4F0' }}>
                    {sub.totalLabel}
                  </td>
                  <td style={{ backgroundColor: '#D6E4F0' }} />
                  <td className="py-1.5 pl-4 text-right font-mono font-semibold text-gray-800 text-xs" style={{ backgroundColor: '#D6E4F0' }}>
                    {formatCurrency(sub.total)}
                  </td>
                  {showTr && (
                    <>
                      <td className="py-1.5 pl-4 text-right font-mono font-semibold text-indigo-700 text-xs" style={{ backgroundColor: '#D6E4F0' }}>
                        {sub.translatedTotal !== undefined ? formatCurrency(sub.translatedTotal) : '—'}
                      </td>
                      <td className="py-1.5 px-4 text-right font-mono text-gray-700 text-xs" style={{ backgroundColor: '#D6E4F0' }}>
                        {sub.fx_rate != null ? sub.fx_rate.toFixed(6) : <span className="text-gray-500">—</span>}
                      </td>
                      <td className="py-1.5 pr-4 text-right" style={{ backgroundColor: '#D6E4F0' }}>
                        {sub.rate_type ? (
                          <span className={clsx(
                            'text-[10px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide',
                            sub.rate_type.toLowerCase() === 'average'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-gray-200 text-gray-700'
                          )}>
                            {sub.rate_type === 'Average' ? 'Avg' : 'Close'}
                          </span>
                        ) : <span className="text-gray-500">—</span>}
                      </td>
                    </>
                  )}
                </tr>
                <tr><td colSpan={colCount} className="h-1" /></tr>
              </React.Fragment>
            ))}
            {/* Grand total row */}
            {section.grandTotal !== null && section.grandTotalLabel && (
              <tr>
                <td className="py-2 pl-2 pr-2 font-bold text-white text-xs" style={{ backgroundColor: '#1F4E79' }}>
                  {section.grandTotalLabel}
                </td>
                <td style={{ backgroundColor: '#1F4E79' }} />
                <td className="py-2 pl-4 text-right font-mono font-bold text-white text-xs" style={{ backgroundColor: '#1F4E79' }}>
                  {formatCurrency(section.grandTotal)}
                </td>
                {showTr && (
                  <>
                    <td className="py-2 pl-4 text-right font-mono font-bold text-white text-xs" style={{ backgroundColor: '#1F4E79' }}>
                      {section.translatedGrandTotal !== null && section.translatedGrandTotal !== undefined
                        ? formatCurrency(section.translatedGrandTotal)
                        : '—'}
                    </td>
                    <td className="py-2 px-4 text-right font-mono text-white text-xs" style={{ backgroundColor: '#1F4E79' }}>
                      {section.fx_rate != null ? section.fx_rate.toFixed(6) : '—'}
                    </td>
                    <td className="py-2 pr-4 text-right font-semibold text-white text-xs" style={{ backgroundColor: '#1F4E79' }}>
                      {section.rate_type ? (
                        <span className={clsx(
                          'text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide',
                          section.rate_type.toLowerCase() === 'average'
                            ? 'bg-amber-500/20 text-amber-100'
                            : 'bg-white/20 text-white'
                        )}>
                          {section.rate_type === 'Average' ? 'Avg' : 'Close'}
                        </span>
                      ) : '—'}
                    </td>
                  </>
                )}
              </tr>
            )}
            <tr><td colSpan={colCount} className="h-3" /></tr>
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

// --- Imbalance Analysis Panel ------------------------------------------------

function ImbalanceAnalysis({ data }: { data: { totals: Record<string, number>; unmapped_accounts?: StatementItem[]; unmapped_count?: number; unmapped_total?: number } }) {
  const totalAssets = (data.totals.total_assets ?? 0);
  const totalEqAndLiab = (data.totals.equity ?? 0)
    + (data.totals.non_current_liabilities ?? 0)
    + (data.totals.current_liabilities ?? 0);
  const difference = Math.round((totalAssets - totalEqAndLiab) * 100) / 100;
  const unmapped = data.unmapped_accounts ?? [];
  const unmappedTotal = data.unmapped_total ?? 0;

  if (Math.abs(difference) < 1) return null;

  return (
    <div className="mx-4 mb-4 rounded-lg border border-red-200 bg-red-50 p-5">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-4">
          <h4 className="text-sm font-bold text-red-800">Balance Sheet Analysis — Out of Balance</h4>

          {/* Summary table */}
          <div className="bg-white/70 rounded-lg border border-red-100 overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-red-100">
                  <td className="py-2 px-4 text-gray-700 font-medium">Total Assets</td>
                  <td className="py-2 px-4 text-right font-mono text-gray-900">{formatCurrency(totalAssets)}</td>
                </tr>
                <tr className="border-b border-red-100">
                  <td className="py-2 px-4 text-gray-700 font-medium">Total Equity & Liabilities</td>
                  <td className="py-2 px-4 text-right font-mono text-gray-900">{formatCurrency(Math.abs(totalEqAndLiab))}</td>
                </tr>
                <tr className="bg-red-100/50">
                  <td className="py-2 px-4 text-red-800 font-bold">Difference</td>
                  <td className="py-2 px-4 text-right font-mono font-bold text-red-700">{formatCurrency(difference)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Root cause */}
          {unmapped.length > 0 && (
            <>
              <div className="text-sm text-red-700">
                <span className="font-semibold">Root Cause:</span>{' '}
                {unmapped.length} unmapped account{unmapped.length > 1 ? 's' : ''} with total balance of{' '}
                <span className="font-mono font-semibold">{formatCurrency(unmappedTotal)}</span>.
                These accounts have no Group Account mapping and cannot be classified into Assets, Liabilities, or Equity.
              </div>

              {/* Unmapped accounts table */}
              <div className="bg-white/70 rounded-lg border border-red-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-red-200 bg-red-50/50">
                      <th className="py-2 px-4 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">Code</th>
                      <th className="py-2 px-4 text-left text-xs font-semibold text-red-700 uppercase tracking-wider">Account Name</th>
                      <th className="py-2 px-4 text-right text-xs font-semibold text-red-700 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmapped.map((item, idx) => (
                      <tr key={idx} className="border-b border-red-50 hover:bg-red-50/30">
                        <td className="py-1.5 px-4 font-mono text-xs text-gray-500">{item.code}</td>
                        <td className="py-1.5 px-4 text-gray-700">{item.name}</td>
                        <td className={clsx('py-1.5 px-4 text-right font-mono', item.balance < 0 ? 'text-red-600' : 'text-gray-900')}>
                          {formatCurrency(item.balance)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-red-100/30 border-t border-red-200">
                      <td className="py-2 px-4" />
                      <td className="py-2 px-4 font-semibold text-gray-800">Total Unmapped</td>
                      <td className="py-2 px-4 text-right font-mono font-bold text-red-700">{formatCurrency(unmappedTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Action guidance */}
          <div className="flex items-start gap-2 bg-white/50 rounded-lg border border-red-100 p-3">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-700 space-y-1">
              <p className="font-semibold">How to fix:</p>
              <p>Go to <strong>Entity COA</strong> and map the unmapped accounts to group accounts using <strong>AI Map</strong> (automatic) or the pencil icon (manual).</p>
              <p>Once all accounts are mapped, the Balance Sheet will balance automatically.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Badge Configs -----------------------------------------------------------

const statusBadgeConfig: Record<string, { label: string; bg: string; text: string }> = {
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-600' },
  validated: { label: 'Validated', bg: 'bg-green-100', text: 'text-green-700' },
  approved: { label: 'Approved', bg: 'bg-blue-100', text: 'text-blue-700' },
};

const sourceBadgeConfig: Record<string, { label: string; bg: string; text: string }> = {
  manual: { label: 'Manual', bg: 'bg-gray-100', text: 'text-gray-600' },
  upload: { label: 'Upload', bg: 'bg-blue-100', text: 'text-blue-600' },
  erp: { label: 'ERP', bg: 'bg-purple-100', text: 'text-purple-600' },
  journal: { label: 'Journal', bg: 'bg-amber-100', text: 'text-amber-700' },
};

const scenarioBadgeConfig: Record<string, { label: string; bg: string; text: string }> = {
  Actual: { label: 'Actual', bg: 'bg-blue-100', text: 'text-blue-700' },
  Budget: { label: 'Budget', bg: 'bg-amber-100', text: 'text-amber-700' },
  Forecast: { label: 'Forecast', bg: 'bg-teal-100', text: 'text-teal-700' },
};

const balanceTypeBadgeConfig: Record<string, { label: string; bg: string; text: string }> = {
  ytd: { label: 'YTD', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  monthly: { label: 'Monthly', bg: 'bg-orange-100', text: 'text-orange-700' },
};

// --- Sortable Header Component -----------------------------------------------

type SortField = 'account_code' | 'account_name' | 'icp_code' | 'debit' | 'credit' | 'net_balance' | 'currency' | 'source' | 'status';
type SortDirection = 'asc' | 'desc';

function SortableHeader({
  label, field, sortField, sortDirection, onSort, className,
}: {
  label: string;
  field: SortField;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}) {
  const isActive = sortField === field;
  return (
    <th
      className={clsx('table-header cursor-pointer select-none hover:bg-gray-100/80 transition-colors', className)}
      onClick={() => onSort(field)}
    >
      <div className={clsx('flex items-center gap-1', className?.includes('text-right') && 'justify-end', className?.includes('text-center') && 'justify-center')}>
        <span>{label}</span>
        {isActive ? (
          sortDirection === 'asc'
            ? <ArrowUp className="w-3 h-3 text-primary-600" />
            : <ArrowDown className="w-3 h-3 text-primary-600" />
        ) : (
          <ChevronsUpDown className="w-3 h-3 text-gray-300" />
        )}
      </div>
    </th>
  );
}

// --- Trial Balances Page -----------------------------------------------------

export default function TrialBalances() {
  const { addNotification } = useAppStore();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [trialBalances, setTrialBalances] = useState<TrialBalance[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Periods and entities from API
  const [periods, setPeriods] = useState<Period[]>([]);
  const [entities, setEntities] = useState<Pick<Entity, 'id' | 'code' | 'name' | 'functional_currency' | 'ownership_pct'>[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedEntity, setSelectedEntity] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showOBUpload, setShowOBUpload] = useState(false);
  const [obUploading, setObUploading] = useState(false);
  const [obResult, setObResult] = useState<{ rows_imported: number; errors: string[]; warnings: string[] } | null>(null);
  const [obFile, setObFile] = useState<File | null>(null);
  const obFileInputRef = useRef<HTMLInputElement>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);
  const bulkFileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ rows_imported: number; errors: string[]; warnings: string[] } | null>(null);
  const [tbAiSuggestion, setTbAiSuggestion] = useState<string | null>(null);
  const [tbAiLoading, setTbAiLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOriginal, setExportOriginal] = useState(true);
  const [exportTranslated, setExportTranslated] = useState(false);
  const [exportIncludeCta, setExportIncludeCta] = useState(false);
  const [exportContext, setExportContext] = useState<'trial_balance' | 'statement'>('trial_balance');

  // Translation state
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationResult, setTranslationResult] = useState<TrialBalanceTranslationResponse | null>(null);
  const [toCurrency, setToCurrency] = useState('');

  // Statement view
  type StatementType = 'balance_sheet' | 'income_statement';
  type IsSection = { label: string; items: StatementItem[]; total: number };
  type StatementData = {
    statement_type: StatementType;
    entity_name: string; currency: string; period_label?: string;
    translated_currency?: string;
    non_current_assets?: StatementItem[]; current_assets?: StatementItem[];
    equity?: StatementItem[]; non_current_liabilities?: StatementItem[]; current_liabilities?: StatementItem[];
    revenue?: StatementItem[]; expenses?: StatementItem[];
    revenue_sections?: IsSection[]; expense_sections?: IsSection[];
    totals: Record<string, number>;
    translated_totals?: Record<string, number>;
    is_balanced?: boolean;
    unmapped_accounts?: StatementItem[];
    unmapped_count?: number;
    unmapped_total?: number;
    warnings?: string[];
  };
  const [activeStatement, setActiveStatement] = useState<StatementType | null>(null);
  const [statementData, setStatementData] = useState<StatementData | null>(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tbTableExpanded, setTbTableExpanded] = useState(true);
  const [statementLevel, setStatementLevel] = useState<StatementLevel>('all');
  // Member filter for NCI split view: '' = all, 'Total' | 'Parent' | 'NCI'
  const [memberFilter] = useState<string>('');

  // Sort state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 100;

  // View mode: 'detail' (per-account) or 'uploads' (grouped uploads list)
  type ViewMode = 'detail' | 'uploads';
  const [viewMode, setViewMode] = useState<ViewMode>('uploads');
  const [isAdjusted, setIsAdjusted] = useState(false);

  // Uploads list state
  const [uploads, setUploads] = useState<TrialBalanceUpload[]>([]);
  const [uploadsTotal, setUploadsTotal] = useState(0);
  const [uploadsPage, setUploadsPage] = useState(1);
  const [uploadsLoading, setUploadsLoading] = useState(false);

  // Upload modal options
  const [uploadScenario, setUploadScenario] = useState('Actual');
  const [uploadBalanceType, setUploadBalanceType] = useState('ytd');

  // Load periods and entities on mount (no auto-selection — page starts blank)
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [periodsRes, entitiesRes] = await Promise.all([
          periodsApi.list(),
          entitiesApi.list({ page: 1, page_size: 200 }),
        ]);
        const periodsArr = Array.isArray(periodsRes) ? periodsRes : ((periodsRes as { items?: Period[] })?.items ?? []);
        const entitiesArr = Array.isArray(entitiesRes) ? entitiesRes : ((entitiesRes as { items?: Entity[] })?.items ?? []);
        setPeriods(periodsArr as Period[]);
        setEntities(entitiesArr as Entity[]);
      } catch {
        // Silently fail on filter load - main data load will show errors
      }
    };
    loadFilters();
  }, []);

  // Load trial balance data when period/entity/page changes
  const loadData = useCallback(async () => {
    if (!selectedPeriod || !selectedEntity) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = {
        period_id: selectedPeriod,
        entity_id: selectedEntity,
        member: memberFilter || undefined,
        page: currentPage,
        page_size: isAdjusted ? 500 : PAGE_SIZE,
      };
      const res = isAdjusted
        ? await trialBalancesApi.adjusted(params)
        : await trialBalancesApi.list(params);
      setTrialBalances(res.items ?? []);
      setTotalCount(res.total ?? 0);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load trial balance data');
      setTrialBalances([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriod, selectedEntity, currentPage, isAdjusted, memberFilter]);

  useEffect(() => {
    if (selectedPeriod && selectedEntity) {
      loadData();
    }
  }, [loadData, selectedPeriod, selectedEntity]);

  // Load uploads list — filtered by selected entity (required) and optionally period
  const loadUploads = useCallback(async () => {
    if (!selectedEntity) return;
    setUploadsLoading(true);
    try {
      const params: Record<string, string | number> = { page: uploadsPage, page_size: PAGE_SIZE, entity_id: selectedEntity };
      if (selectedPeriod) params.period_id = selectedPeriod;
      const res = await trialBalancesApi.listUploads(params as Parameters<typeof trialBalancesApi.listUploads>[0]);
      setUploads(res.items ?? []);
      setUploadsTotal(res.total ?? 0);
    } catch {
      setUploads([]);
      setUploadsTotal(0);
    } finally {
      setUploadsLoading(false);
    }
  }, [uploadsPage, selectedEntity, selectedPeriod]);

  useEffect(() => {
    if (viewMode === 'uploads' && selectedEntity) {
      loadUploads();
    }
  }, [viewMode, loadUploads]);

  // Inject synthetic CTA row when translation is active.
  // Filter out any existing DB CTA rows (999999) to avoid duplicates —
  // the synthetic row uses the correct balancing figure.
  const tbWithCta = useMemo(() => {
    if (!translationResult || Number(translationResult.cta_amount) === 0) return trialBalances;
    const entity = entities.find((e) => String(e.id) === String(selectedEntity));
    const ctaRow = buildCtaSyntheticRow(Number(translationResult.cta_amount), entity?.functional_currency ?? '');
    const withoutDbCta = trialBalances.filter((tb) => tb.account_code !== CTA_ACCOUNT_CODE);
    return [...withoutDbCta, ctaRow];
  }, [trialBalances, translationResult, entities, selectedEntity]);

  const translationRowsWithCta = useMemo((): TranslatedTrialBalanceRow[] => {
    if (!translationResult) return [];
    if (Number(translationResult.cta_amount) === 0) return translationResult.rows;
    const ctaTranslatedRow = buildCtaTranslatedRow(
      Number(translationResult.cta_amount),
      translationResult.from_currency,
      translationResult.closing_rate,
    );
    return [...translationResult.rows, ctaTranslatedRow];
  }, [translationResult]);

  // Calculate totals — exclude CTA rows (999999) since CTA is a synthetic balancing figure
  const tbForTotals = trialBalances.filter((tb) => tb.account_code !== CTA_ACCOUNT_CODE);
  const totalDebits = tbForTotals.reduce((sum, tb) => sum + Number(isAdjusted ? (tb.adjusted_debit ?? tb.debit) : tb.debit), 0);
  const totalCredits = tbForTotals.reduce((sum, tb) => sum + Number(isAdjusted ? (tb.adjusted_credit ?? tb.credit) : tb.credit), 0);
  const totalJournalDebits = tbForTotals.reduce((sum, tb) => sum + Number(tb.journal_debit ?? 0), 0);
  const totalJournalCredits = tbForTotals.reduce((sum, tb) => sum + Number(tb.journal_credit ?? 0), 0);
  const difference = totalDebits - totalCredits;
  const isBalanced = Math.abs(difference) < 0.01;

  // Filter (use tbWithCta so CTA row appears as a regular row when translation is active)
  const filtered = tbWithCta.filter((tb) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (tb.account_code || '').toLowerCase().includes(q) ||
        (tb.account_name || '').toLowerCase().includes(q) ||
        (tb.icp_code || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // Sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedFiltered = React.useMemo(() => {
    if (!sortField) return filtered;
    return [...filtered].sort((a, b) => {
      const dir = sortDirection === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'account_code': return (a.account_code || '').localeCompare(b.account_code || '') * dir;
        case 'account_name': return (a.account_name || '').localeCompare(b.account_name || '') * dir;
        case 'icp_code': return (a.icp_code || '').localeCompare(b.icp_code || '') * dir;
        case 'debit': return (Number(a.debit) - Number(b.debit)) * dir;
        case 'credit': return (Number(a.credit) - Number(b.credit)) * dir;
        case 'net_balance': return ((Number(a.debit) - Number(a.credit)) - (Number(b.debit) - Number(b.credit))) * dir;
        case 'currency': return (a.currency || '').localeCompare(b.currency || '') * dir;
        case 'source': return (a.source || '').localeCompare(b.source || '') * dir;
        case 'status': return (a.status || '').localeCompare(b.status || '') * dir;
        default: return 0;
      }
    });
  }, [filtered, sortField, sortDirection]);

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const openUploadModal = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setTbAiSuggestion(null);
    setTbAiLoading(false);
    setUploadScenario('Actual');
    setUploadBalanceType('ytd');
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowUpload(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedEntity || !selectedPeriod) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const result = await trialBalancesApi.upload(selectedEntity, selectedPeriod, selectedFile, uploadScenario, uploadBalanceType);
      addNotification({
        type: result.errors.length > 0 ? 'warning' : 'success',
        title: 'Trial Balance Uploaded',
        message: `${result.rows_imported} rows imported.${result.warnings.length > 0 ? ` ${result.warnings.length} warning(s).` : ''}${result.errors.length > 0 ? ` ${result.errors.length} error(s).` : ''}`,
      });
      // Close modal and reset all upload state
      setShowUpload(false);
      setSelectedFile(null);
      setUploadResult(null);
      setTbAiSuggestion(null);
      setUploadScenario('Actual');
      setUploadBalanceType('ytd');
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Refresh data to show uploaded entries
      await loadData();
      if (viewMode === 'uploads') await loadUploads();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      const errMsg = apiErr.detail || 'Failed to upload trial balance.';
      setUploadResult({ rows_imported: 0, errors: [errMsg], warnings: [] });
      addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: errMsg,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedEntity || !selectedPeriod) return;
    setValidating(true);
    setValidationResult(null);
    try {
      const result = await trialBalancesApi.validate(selectedEntity, selectedPeriod);
      setValidationResult(result);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setValidationResult({
        success: false,
        message: apiErr.detail || 'Validation failed.',
      });
    } finally {
      setValidating(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!selectedEntity) return;
    setIsDownloadingTemplate(true);
    try {
      const blob = await trialBalancesApi.downloadTemplate(selectedEntity);
      const entity = entities.find((e) => String(e.id) === String(selectedEntity));
      const entityCode = entity?.code ?? 'entity';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tb_template_${entityCode}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      addNotification({ type: 'error', title: 'Download Failed', message: 'Could not download template.' });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleOpenStatement = async (type: 'balance_sheet' | 'income_statement') => {
    if (!selectedEntity || !selectedPeriod) return;
    setActiveStatement(type);
    setStatementLevel('all');
    setStatementLoading(true);
    setStatementData(null);
    try {
      // Pass toCurrency if translation is active so the statement loads with translated values
      const translationActiveCurrency = translationResult && toCurrency ? toCurrency : undefined;
      const data = await trialBalancesApi.getStatement(selectedEntity, selectedPeriod, type, translationActiveCurrency);

      // Inject CTA into statement when translation is active
      const ctaAmt = translationResult ? Number(translationResult.cta_amount) : 0;
      if (ctaAmt !== 0 && translationActiveCurrency) {
        const ctaItem: StatementItem = {
          code: CTA_ACCOUNT_CODE,
          name: CTA_ACCOUNT_NAME,
          fs_line_item: 'Other Income Expense',
          balance: 0,
          translated_balance: ctaAmt,
          rate_type: 'Closing',
          fx_rate: translationResult?.closing_rate ?? undefined,
        };

        if (type === 'income_statement') {
          // CTA does NOT impact P&L per IAS 21 — it is an unrealised
          // translation difference that belongs in OCI / Equity only.
        } else {
          // Balance Sheet: inject into equity section as Translation Reserve
          ctaItem.fs_line_item = 'Translation Reserve (OCI)';
          data.equity = [...(data.equity ?? []), ctaItem];
          if (data.translated_totals) {
            data.translated_totals.equity = (data.translated_totals.equity ?? 0) + ctaAmt;
          }
        }
      }

      setStatementData(data);
    } catch {
      addNotification({ type: 'error', title: 'Error', message: 'Failed to load statement.' });
      setActiveStatement(null);
    } finally {
      setStatementLoading(false);
    }
  };

  const handleExportStatement = () => {
    if (!selectedEntity || !selectedPeriod || !activeStatement) return;
    setExportOriginal(true);
    setExportTranslated(false);
    setExportIncludeCta(false);
    setExportContext('statement');
    setShowExportModal(true);
  };

  const handleExport = () => {
    setExportOriginal(true);
    setExportTranslated(false);
    setExportIncludeCta(false);
    setExportContext('trial_balance');
    setShowExportModal(true);
  };

  const submitExport = async () => {
    if (!selectedEntity || !selectedPeriod) return;
    setShowExportModal(false);
    setIsExporting(true);
    try {
      let blob: Blob;
      const entity = entities.find((e) => String(e.id) === String(selectedEntity));
      const period = periods.find((p) => String(p.id) === String(selectedPeriod));
      const periodLabel = period ? `${period.year}-${String(period.month).padStart(2, '0')}` : 'export';
      const entityCode = entity?.code ?? 'entity';

      if (exportContext === 'statement' && activeStatement) {
        const levelParam = statementLevel === 'all' ? undefined : (statementLevel as number);
        blob = await trialBalancesApi.exportStatement(selectedEntity, selectedPeriod, activeStatement, {
          level: levelParam,
          to_currency: exportTranslated && toCurrency ? toCurrency : undefined,
          export_original: exportOriginal,
          export_translated: exportTranslated && Boolean(toCurrency),
        });
        const levelSuffix = statementLevel === 'all' ? '' : `_L${statementLevel}`;
        const trSuffix = exportTranslated && toCurrency ? `_${toCurrency}` : '';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${activeStatement}_${entityCode}_${periodLabel}${levelSuffix}${trSuffix}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        blob = await exportsApi.trialBalance({
          report_type: 'trial_balance',
          entity_id: Number(selectedEntity),
          period_id: Number(selectedPeriod),
          format: 'xlsx',
          export_original: exportOriginal,
          export_translated: exportTranslated,
          include_cta: exportIncludeCta,
          to_currency: exportTranslated ? toCurrency : undefined,
        });
        const suffix = exportTranslated ? `_${toCurrency}` : '';
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `trial_balance_${entityCode}_${periodLabel}${suffix}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: apiErr.detail || 'Failed to export.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const currentEntity = entities.find((e) => String(e.id) === String(selectedEntity));


  // Delete upload handler (admin only)
  const [deletingUpload, setDeletingUpload] = useState<string | null>(null);
  const handleDeleteUpload = async (upload: TrialBalanceUpload) => {
    if (!confirm(`Delete all trial balance data for ${upload.entity_name} — ${upload.period_label} (${upload.scenario})?\n\nThis cannot be undone.`)) return;
    const key = `${upload.entity_id}-${upload.period_id}-${upload.scenario}`;
    setDeletingUpload(key);
    try {
      await trialBalancesApi.deleteUpload(upload.entity_id, upload.period_id, upload.scenario);
      addNotification({ type: 'success', title: 'Upload Deleted', message: `Deleted TB data for ${upload.entity_name} — ${upload.period_label}` });
      await loadUploads();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete upload.' });
    } finally {
      setDeletingUpload(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trial Balances</h1>
          <p className="text-sm text-gray-500 mt-1">Upload and manage entity trial balance data</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5 mr-2">
            <button
              onClick={() => setViewMode('uploads')}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                viewMode === 'uploads'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <List className="w-3.5 h-3.5" />
              Uploads
            </button>
            <button
              onClick={() => setViewMode('detail')}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                viewMode === 'detail'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <Eye className="w-3.5 h-3.5" />
              Detail
            </button>
          </div>
          {viewMode === 'detail' && selectedEntity && selectedPeriod && (
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setIsAdjusted(false)}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  !isAdjusted ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                )}
              >
                Unadjusted
              </button>
              <button
                onClick={() => setIsAdjusted(true)}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  isAdjusted ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                )}
              >
                Adjusted
              </button>
            </div>
          )}
          {viewMode === 'detail' && trialBalances.length > 0 && (
            <>
              <button
                onClick={() => handleOpenStatement('balance_sheet')}
                className={clsx('btn-secondary', activeStatement === 'balance_sheet' && 'ring-2 ring-primary-400')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Balance Sheet
              </button>
              <button
                onClick={() => handleOpenStatement('income_statement')}
                className={clsx('btn-secondary', activeStatement === 'income_statement' && 'ring-2 ring-primary-400')}
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Income Statement
              </button>
            </>
          )}
          <button
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate || !selectedEntity}
            className="btn-secondary"
            title="Download a pre-filled Excel template for the selected entity"
          >
            {isDownloadingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download Template
          </button>
          <button onClick={handleValidate} disabled={validating || trialBalances.length === 0} className="btn-secondary">
            {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Validate
          </button>
          <button onClick={() => { setShowOBUpload(true); setObResult(null); setObFile(null); }} className="btn-secondary" title="Upload prior-year closing balances as opening balances for the selected period">
            <Upload className="w-4 h-4 mr-2" />
            Upload Opening Balances
          </button>
          <button onClick={() => { setShowBulkUpload(true); setBulkFiles([]); setBulkResult(null); }} className="btn-secondary" title="Upload multiple monthly TB files at once — month is auto-detected from filename">
            <Layers className="w-4 h-4 mr-2" />
            Bulk Upload
          </button>
          <div className="flex items-center gap-2 border-l border-gray-200 pl-2 ml-1">
            <button
              onClick={async () => {
                if (!selectedEntity || !selectedPeriod || !toCurrency) return;
                setIsTranslating(true);
                setTranslationResult(null);
                try {
                  const result = await trialBalancesApi.translate({
                    entity_id: selectedEntity,
                    period_id: selectedPeriod,
                    to_currency: toCurrency,
                  });
                  setTranslationResult(result);
                } catch (err: unknown) {
                  const apiErr = err as { detail?: string; message?: string };
                  addNotification({ type: 'error', title: 'Translation Failed', message: apiErr.detail || 'Could not translate trial balance.' });
                } finally {
                  setIsTranslating(false);
                }
              }}
              disabled={isTranslating || !selectedEntity || !selectedPeriod || trialBalances.length === 0 || !toCurrency}
              className="btn-secondary py-1.5 px-3"
              title="Translate to selected currency"
            >
              {isTranslating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <DollarSign className="w-4 h-4 mr-1" />}
              Translate
            </button>
          </div>
          <button onClick={openUploadModal} className="btn-primary">
            <Upload className="w-4 h-4 mr-2" />
            Upload Trial Balance
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500 uppercase">Entity</label>
          <SearchableSelect
            className="w-64"
            options={entities.map((e) => ({ value: String(e.id), label: e.name, secondary: e.code }))}
            value={selectedEntity}
            onChange={(v) => { setSelectedEntity(v); setSelectedPeriod(''); setCurrentPage(1); setUploadsPage(1); setTrialBalances([]); setUploads([]); setToCurrency('AED'); setTranslationResult(null); }}
            placeholder="Select Entity"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-500 uppercase">Period</label>
          <select
            className="select-field text-sm w-40"
            value={selectedPeriod}
            onChange={(e) => { setSelectedPeriod(e.target.value); setCurrentPage(1); setUploadsPage(1); }}
            disabled={!selectedEntity}
          >
            <option value="">All Periods</option>
            {periods.map((p) => (
              <option key={p.id} value={String(p.id)}>{periodDisplayName(p)}</option>
            ))}
          </select>
        </div>
        {viewMode === 'detail' && (
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="input-field pl-9 text-sm"
              placeholder="Search by account or ICP code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
        {/* 
        {viewMode === 'detail' && (
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
            {([
              '', 
              'Total', 
              'Parent', 
              'NCI'
            ] as const).map((m) => {
              const cfg = m ? memberBadgeConfig[m] : null;
              return (
                <button
                  key={m || 'all'}
                  onClick={() => setMemberFilter(m)}
                  className={clsx(
                    'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                    memberFilter === m
                      ? (cfg ? `${cfg.bg} ${cfg.text} shadow-sm` : 'bg-white text-gray-700 shadow-sm')
                      : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  {m || 'All'}
                </button>
              );
            })}
          </div>
        )}
        */}
      </div>

      {/* ==================== BLANK STATE — No entity selected ==================== */}
      {!selectedEntity && (
        <div className="card py-20 text-center">
          <Layers className="w-14 h-14 mx-auto text-gray-200 mb-4" />
          <h3 className="text-base font-semibold text-gray-700">Select an Entity to get started</h3>
          <p className="text-sm text-gray-400 mt-1.5">Choose an entity from the dropdown above to view its uploaded trial balances</p>
        </div>
      )}

      {/* ==================== UPLOADS LIST VIEW ==================== */}
      {viewMode === 'uploads' && selectedEntity && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">Uploaded Trial Balances</span>
              <span className="badge bg-gray-100 text-gray-600">{uploadsTotal} upload{uploadsTotal !== 1 ? 's' : ''}</span>
            </div>
            <button
              onClick={loadUploads}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              <RefreshCw className={clsx('w-3.5 h-3.5', uploadsLoading && 'animate-spin')} />
              Refresh
            </button>
          </div>

          {uploadsLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 animate-pulse">
                  <div className="skeleton w-32 h-4" />
                  <div className="skeleton w-20 h-4" />
                  <div className="skeleton w-16 h-4" />
                  <div className="skeleton flex-1 h-4" />
                  <div className="skeleton w-16 h-4" />
                  <div className="skeleton w-20 h-4" />
                  <div className="skeleton w-20 h-4" />
                </div>
              ))}
            </div>
          ) : uploads.length === 0 ? (
            <div className="py-16 text-center">
              <Table2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-sm font-medium text-gray-900">No uploads found</h3>
              <p className="text-sm text-gray-500 mt-1">Upload a trial balance file to get started</p>
              <button onClick={openUploadModal} className="btn-primary mt-4">
                <Upload className="w-4 h-4 mr-2" />
                Upload Now
              </button>
            </div>
          ) : (
            <>
              <div className="overflow-auto max-h-[calc(100vh-250px)]">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e5e7eb]">
                    <tr className="border-b border-gray-200">
                      <th className="table-header">Entity</th>
                      <th className="table-header">Period</th>
                      <th className="table-header text-center">Scenario</th>
                      <th className="table-header">Filename</th>
                      <th className="table-header text-center">Balance Type</th>
                      <th className="table-header text-center">Status</th>
                      <th className="table-header text-center">Rows</th>
                      <th className="table-header text-right">Total Debits</th>
                      <th className="table-header text-right">Total Credits</th>
                      <th className="table-header text-center">Balanced</th>
                      {isAdmin && <th className="table-header w-16"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {uploads.map((u, idx) => {
                      const scBadge = scenarioBadgeConfig[u.scenario] || scenarioBadgeConfig.Actual;
                      const btBadge = balanceTypeBadgeConfig[u.balance_type] || balanceTypeBadgeConfig.ytd;
                      const sBadge = statusBadgeConfig[u.status] || statusBadgeConfig.draft;
                      return (
                        <tr
                          key={`${u.entity_id}-${u.period_id}-${u.scenario}-${u.balance_type}-${u.filename}-${idx}`}
                          className={clsx(
                            'hover:bg-blue-50/40 transition-colors cursor-pointer',
                            idx % 2 === 1 && 'bg-gray-50/40',
                          )}
                          onClick={() => {
                            // Navigate to detail view for this entity/period
                            setSelectedEntity(String(u.entity_id));
                            setSelectedPeriod(String(u.period_id));
                            setViewMode('detail');
                            setCurrentPage(1);
                          }}
                        >
                          <td className="table-cell">
                            <div>
                              <span className="text-sm font-medium text-gray-900">{u.entity_name}</span>
                              <span className="ml-1.5 text-xs text-gray-400 font-mono">{u.entity_code}</span>
                            </div>
                          </td>
                          <td className="table-cell text-sm text-gray-700">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              {u.period_label}
                            </div>
                          </td>
                          <td className="table-cell text-center">
                            <span className={clsx('inline-block px-2 py-0.5 rounded-full text-xs font-medium', scBadge.bg, scBadge.text)}>
                              {scBadge.label}
                            </span>
                          </td>
                          <td className="table-cell text-sm text-gray-700">
                            <div className="flex items-center gap-1.5 max-w-[200px]">
                              <FileSpreadsheet className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{u.filename || '—'}</span>
                            </div>
                          </td>
                          <td className="table-cell text-center">
                            <button
                              title={`Click to switch to ${u.balance_type === 'ytd' ? 'Monthly' : 'YTD'}`}
                              className={clsx(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-all hover:ring-2 hover:ring-offset-1',
                                btBadge.bg, btBadge.text,
                                u.balance_type === 'ytd' ? 'hover:ring-indigo-300' : 'hover:ring-emerald-300',
                              )}
                              onClick={async (e) => {
                                e.stopPropagation();
                                const newType = u.balance_type === 'ytd' ? 'monthly' : 'ytd';
                                try {
                                  await trialBalancesApi.updateBalanceType({
                                    entity_id: u.entity_id,
                                    period_id: u.period_id,
                                    balance_type: newType,
                                    scenario: u.scenario,
                                    filename: u.filename,
                                  });
                                  await loadUploads();
                                } catch {
                                  // silently fail — user sees no change
                                }
                              }}
                            >
                              {u.balance_type === 'ytd'
                                ? <ToggleLeft className="w-3 h-3" />
                                : <ToggleRight className="w-3 h-3" />}
                              {btBadge.label}
                            </button>
                          </td>
                          <td className="table-cell text-center">
                            <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', sBadge.bg, sBadge.text)}>
                              {u.status === 'validated' || u.status === 'approved'
                                ? <CheckCircle2 className="w-3 h-3" />
                                : <AlertCircle className="w-3 h-3" />}
                              {sBadge.label}
                            </span>
                          </td>
                          <td className="table-cell text-center font-mono text-sm text-gray-700">{u.row_count}</td>
                          <td className="table-cell text-right font-mono text-sm text-gray-900">{formatCurrency(u.total_debits)}</td>
                          <td className="table-cell text-right font-mono text-sm text-gray-900">{formatCurrency(u.total_credits)}</td>
                          <td className="table-cell text-center">
                            {Math.abs(Math.round(u.total_debits) - Math.round(u.total_credits)) < 2 ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500 mx-auto" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                            )}
                          </td>
                          {isAdmin && (
                            <td className="table-cell text-center">
                              <button
                                title="Delete this upload"
                                onClick={(e) => { e.stopPropagation(); handleDeleteUpload(u); }}
                                disabled={deletingUpload === `${u.entity_id}-${u.period_id}-${u.scenario}`}
                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-40"
                              >
                                {deletingUpload === `${u.entity_id}-${u.period_id}-${u.scenario}` ? (
                                  <Loader2 className="w-3.5 h-3.5 text-red-400 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                                )}
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Uploads pagination */}
              {Math.ceil(uploadsTotal / PAGE_SIZE) > 1 && (
                <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Showing {((uploadsPage - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(uploadsPage * PAGE_SIZE, uploadsTotal)} of {uploadsTotal}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setUploadsPage(1)}
                      disabled={uploadsPage === 1}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      First
                    </button>
                    <button
                      onClick={() => setUploadsPage((p) => Math.max(1, p - 1))}
                      disabled={uploadsPage === 1}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-3 py-1 text-gray-700 font-medium">
                      Page {uploadsPage} of {Math.ceil(uploadsTotal / PAGE_SIZE)}
                    </span>
                    <button
                      onClick={() => setUploadsPage((p) => Math.min(Math.ceil(uploadsTotal / PAGE_SIZE), p + 1))}
                      disabled={uploadsPage >= Math.ceil(uploadsTotal / PAGE_SIZE)}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setUploadsPage(Math.ceil(uploadsTotal / PAGE_SIZE))}
                      disabled={uploadsPage >= Math.ceil(uploadsTotal / PAGE_SIZE)}
                      className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== DETAIL VIEW ==================== */}
      {viewMode === 'detail' && selectedEntity && (
        <>
          {/* Summary Stats Cards */}
          {!isLoading && !error && trialBalances.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {/* Total Accounts */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Accounts</span>
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Hash className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold text-gray-900">{totalCount}</span>
                <p className="text-xs text-gray-400 mt-1">{sortedFiltered.length} shown</p>
              </div>
              {/* Total Debits */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Debits</span>
                  <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold text-gray-900">{formatCurrency(totalDebits)}</span>
              </div>
              {/* Total Credits */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Credits</span>
                  <div className="w-8 h-8 rounded-lg bg-purple-500 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-2xl font-bold text-gray-900">{formatCurrency(totalCredits)}</span>
              </div>
              {/* Balance Status */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</span>
                  <div className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', isBalanced ? 'bg-green-500' : 'bg-red-500')}>
                    {isBalanced
                      ? <CheckCircle2 className="w-4 h-4 text-white" />
                      : <AlertTriangle className="w-4 h-4 text-white" />}
                  </div>
                </div>
                {isBalanced ? (
                  <span className="text-2xl font-bold text-green-600">Balanced</span>
                ) : (
                  <>
                    <span className="text-2xl font-bold text-red-600">{formatCurrency(Math.abs(difference))}</span>
                    <p className="text-xs text-red-400 mt-1">Out of balance</p>
                  </>
                )}
              </div>
              {/* Net Position */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Net Position</span>
                  <div className="w-8 h-8 rounded-lg bg-indigo-500 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className={clsx('text-2xl font-bold', difference >= 0 ? 'text-blue-700' : 'text-red-600')}>
                  {formatCurrency(Math.abs(difference))}
                </span>
                <p className="text-xs text-gray-400 mt-1">{difference >= 0 ? 'Dr' : 'Cr'} balance</p>
              </div>
            </div>
          )}

          {/* Validation result */}
          {validationResult && (
            <div className={clsx(
              'px-4 py-3 rounded-lg border flex items-start gap-3',
              validationResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200',
            )}>
              {validationResult.success ? (
                <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={clsx('text-sm font-medium', validationResult.success ? 'text-green-800' : 'text-red-800')}>
                  {validationResult.message}
                </p>
              </div>
              <button onClick={() => setValidationResult(null)} className="ml-auto p-1 hover:bg-white/50 rounded">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="card p-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
              <p className="text-sm text-red-600">{error}</p>
              <button onClick={loadData} className="btn-primary mt-4">Retry</button>
            </div>
          )}

          {/* Trial Balance Table */}
          {!error && isLoading ? (
            <div className="card">
              <div className="p-4 space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 animate-pulse">
                    <div className="skeleton w-16 h-4" />
                    <div className="skeleton flex-1 h-4" />
                    <div className="skeleton w-24 h-4" />
                    <div className="skeleton w-24 h-4" />
                  </div>
                ))}
              </div>
            </div>
          ) : !error && trialBalances.length === 0 ? (
            <div className="card py-16 text-center">
              <Table2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-sm font-medium text-gray-900">No trial balance data</h3>
              <p className="text-sm text-gray-500 mt-1">Upload a trial balance file to get started</p>
              <button onClick={openUploadModal} className="btn-primary mt-4">
                <Upload className="w-4 h-4 mr-2" />
                Upload Now
              </button>
            </div>
          ) : !error ? (
            <div className="card overflow-hidden">
              {/* Entity header — clickable to collapse/expand */}
              <div
                className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between cursor-pointer select-none hover:bg-gray-100/60 transition-colors"
                onClick={() => setTbTableExpanded((v) => !v)}
              >
                <div className="flex items-center gap-3">
                  {tbTableExpanded
                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                    : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <FileSpreadsheet className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">{currentEntity?.name}</span>
                  <span className="badge bg-gray-100 text-gray-600">{currentEntity?.functional_currency}</span>
                  {!tbTableExpanded && (
                    <span className="text-xs text-gray-400 ml-2">
                      {sortedFiltered.length} accounts &middot; Dr {formatCurrency(totalDebits)} &middot; Cr {formatCurrency(totalCredits)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {isBalanced ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Balanced
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs font-medium text-red-600">
                      <AlertTriangle className="w-3.5 h-3.5" /> Out of Balance
                    </span>
                  )}
                </div>
              </div>

              {tbTableExpanded && (
                <>
                  <div className="overflow-auto max-h-[calc(100vh-350px)]">
                    <table className="w-full">
                      <thead className="sticky top-0 z-10 bg-white shadow-[inset_0_-1px_0_0_#e5e7eb]">
                        <tr className="border-b border-gray-200">
                          <SortableHeader label="Account Code" field="account_code" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="w-28" />
                          <SortableHeader label="Account Name" field="account_name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="ICP Code" field="icp_code" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="w-24" />
                          <SortableHeader label={`Debit (${currentEntity?.functional_currency || 'Local'})`} field="debit" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right w-32" />
                          <SortableHeader label={`Credit (${currentEntity?.functional_currency || 'Local'})`} field="credit" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right w-32" />
                          {isAdjusted && (
                            <>
                              <th className="table-header text-right w-28 whitespace-nowrap text-amber-700">JE Debit</th>
                              <th className="table-header text-right w-28 whitespace-nowrap text-amber-700">JE Credit</th>
                            </>
                          )}
                          {translationResult && (
                            <>
                              <th className="table-header text-right w-32 whitespace-nowrap">
                                Debit ({toCurrency})
                              </th>
                              <th className="table-header text-right w-32 whitespace-nowrap">
                                Credit ({toCurrency})
                              </th>
                              <th className="table-header text-right w-24 whitespace-nowrap">
                                FX Rate
                              </th>
                            </>
                          )}
                          <SortableHeader label="Net Balance" field="net_balance" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-right w-32" />
                          <SortableHeader label="Currency" field="currency" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center w-20" />
                          <SortableHeader label="Source" field="source" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center w-20" />
                          {/* 
                          <SortableHeader label="Status" field="status" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} className="text-center w-24" />
                          <th className="table-header text-center w-20">Member</th>
                          */}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {sortedFiltered.map((tb, idx) => {
                          const displayDebit = isAdjusted ? Number(tb.adjusted_debit ?? tb.debit) : Number(tb.debit);
                          const displayCredit = isAdjusted ? Number(tb.adjusted_credit ?? tb.credit) : Number(tb.credit);
                          const net = displayDebit - displayCredit;
                          const srcBadge = sourceBadgeConfig[tb.source] || sourceBadgeConfig.manual;
                          const translatedRow = translationResult ? translationRowsWithCta.find((r) => r.account_code === tb.account_code) : undefined;
                          const hasJeImpact = (tb.journal_count ?? 0) > 0;
                          const isCtaRow = tb.account_code === CTA_ACCOUNT_CODE;
                          return (
                            <tr key={tb.id ?? `je-${idx}`} className={clsx(
                              'hover:bg-blue-50/40 transition-colors',
                              isCtaRow ? 'bg-indigo-50/70 border-l-2 border-indigo-400' :
                              isAdjusted && tb.is_journal_only ? 'bg-amber-50/60 border-l-2 border-amber-400' :
                                isAdjusted && hasJeImpact ? 'bg-amber-50/30' :
                                  isIcpCode(tb.icp_code) ? 'bg-primary-50/50' :
                                    idx % 2 === 1 ? 'bg-gray-50/40' : ''
                            )}>
                              <td className="table-cell font-mono text-xs font-medium text-gray-600">
                                {tb.account_code || '\u2014'}
                                {isCtaRow && (
                                  <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-indigo-100 text-indigo-700">CTA</span>
                                )}
                                {isAdjusted && tb.is_journal_only && (
                                  <span className="ml-1 text-[10px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700">JE</span>
                                )}
                              </td>
                              <td className="table-cell text-gray-900 text-sm">{tb.account_name || '\u2014'}</td>
                              <td className="table-cell font-mono text-xs text-gray-600">{tb.icp_code || '\u2014'}</td>
                              <td className="table-cell text-right font-mono text-sm">
                                {displayDebit > 0 ? (
                                  <span className="text-gray-900">{formatCurrency(displayDebit)}</span>
                                ) : (
                                  <span className="text-gray-300">&mdash;</span>
                                )}
                              </td>
                              <td className="table-cell text-right font-mono text-sm">
                                {displayCredit > 0 ? (
                                  <span className="text-gray-900">{formatCurrency(displayCredit)}</span>
                                ) : (
                                  <span className="text-gray-300">&mdash;</span>
                                )}
                              </td>
                              {isAdjusted && (
                                <>
                                  <td className="table-cell text-right font-mono text-sm">
                                    {Number(tb.journal_debit ?? 0) > 0 ? (
                                      <span className="text-amber-700 font-medium">{formatCurrency(Number(tb.journal_debit))}</span>
                                    ) : (
                                      <span className="text-gray-300">&mdash;</span>
                                    )}
                                  </td>
                                  <td className="table-cell text-right font-mono text-sm">
                                    {Number(tb.journal_credit ?? 0) > 0 ? (
                                      <span className="text-amber-700 font-medium">{formatCurrency(Number(tb.journal_credit))}</span>
                                    ) : (
                                      <span className="text-gray-300">&mdash;</span>
                                    )}
                                  </td>
                                </>
                              )}
                              {translationResult && (
                                <>
                                  <td className="table-cell text-right font-mono text-sm">
                                    {translatedRow && Number(translatedRow.translated_debit) > 0 ? (
                                      <span className="text-gray-900">{formatCurrency(Number(translatedRow.translated_debit))}</span>
                                    ) : (
                                      <span className="text-gray-300">&mdash;</span>
                                    )}
                                  </td>
                                  <td className="table-cell text-right font-mono text-sm">
                                    {translatedRow && Number(translatedRow.translated_credit) > 0 ? (
                                      <span className="text-gray-900">{formatCurrency(Number(translatedRow.translated_credit))}</span>
                                    ) : (
                                      <span className="text-gray-300">&mdash;</span>
                                    )}
                                  </td>
                                  <td className="table-cell text-right">
                                    {translatedRow ? (
                                      <div className="flex flex-col items-end gap-0.5">
                                        <span className="font-mono text-xs text-gray-700">{Number(translatedRow.rate).toFixed(6)}</span>
                                        <span className={clsx(
                                          'text-[10px] font-semibold px-1 py-0.5 rounded uppercase tracking-wide',
                                          translatedRow.rate_type === 'average'
                                            ? 'bg-amber-100 text-amber-700'
                                            : 'bg-gray-100 text-gray-600'
                                        )}>
                                          {translatedRow.rate_type === 'average' ? 'Avg' : 'Close'}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-300">&mdash;</span>
                                    )}
                                  </td>
                                </>
                              )}
                              <td className="table-cell text-right font-mono text-sm">
                                {net !== 0 ? (
                                  <span className={net > 0 ? 'text-blue-700' : 'text-red-600'}>{formatCurrency(net)}</span>
                                ) : (
                                  <span className="text-gray-300">&mdash;</span>
                                )}
                              </td>
                              <td className="table-cell text-center">
                                <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">{tb.currency}</span>
                              </td>
                              <td className="table-cell text-center">
                                <span className={clsx('inline-block px-2 py-0.5 rounded-full text-xs font-medium', srcBadge.bg, srcBadge.text)}>
                                  {srcBadge.label}
                                </span>
                              </td>
                              {/* 
                              <td className="table-cell text-center">
                                <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', sBadge.bg, sBadge.text)}>
                                  {tb.status === 'validated' || tb.status === 'approved'
                                    ? <CheckCircle2 className="w-3 h-3" />
                                    : <AlertCircle className="w-3 h-3" />}
                                  {sBadge.label}
                                </span>
                              </td>
                              <td className="table-cell text-center">
                                {(() => {
                                  const m = tb.member || 'Total';
                                  const mBadge = memberBadgeConfig[m] || memberBadgeConfig.Total;
                                  return (
                                    <span className={clsx('inline-block px-2 py-0.5 rounded-full text-xs font-semibold', mBadge.bg, mBadge.text)}>
                                      {mBadge.label}
                                    </span>
                                  );
                                })()}
                              </td>
                              */}
                            </tr>
                          );
                        })}
                      </tbody>
                      {/* Summary row */}
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                          <td className="table-cell"></td>
                          <td className="table-cell text-gray-900">Total</td>
                          <td className="table-cell"></td>
                          <td className="table-cell text-right font-mono text-sm text-gray-900">{formatCurrency(totalDebits)}</td>
                          <td className="table-cell text-right font-mono text-sm text-gray-900">{formatCurrency(totalCredits)}</td>
                          {isAdjusted && (
                            <>
                              <td className="table-cell text-right font-mono text-sm text-amber-700 font-semibold">{formatCurrency(totalJournalDebits)}</td>
                              <td className="table-cell text-right font-mono text-sm text-amber-700 font-semibold">{formatCurrency(totalJournalCredits)}</td>
                            </>
                          )}
                          {translationResult && (() => {
                            const ctaAmt = Number(translationResult.cta_amount);
                            const adjDebit = Number(translationResult.total_translated_debit) + (ctaAmt < 0 ? Math.abs(ctaAmt) : 0);
                            const adjCredit = Number(translationResult.total_translated_credit) + (ctaAmt > 0 ? ctaAmt : 0);
                            return (
                              <>
                                <td className="table-cell text-right font-mono text-sm font-bold text-gray-900">
                                  {formatCurrency(adjDebit)}
                                </td>
                                <td className="table-cell text-right font-mono text-sm font-bold text-gray-900">
                                  {formatCurrency(adjCredit)}
                                </td>
                                <td className="table-cell"></td>
                              </>
                            );
                          })()}
                          <td className="table-cell text-right font-mono text-sm text-gray-900">{formatCurrency(totalDebits - totalCredits)}</td>
                          <td className="table-cell" colSpan={1}></td>
                        </tr>
                        {!isBalanced && (
                          <tr className="bg-red-50">
                            <td className="table-cell"></td>
                            <td className="table-cell"></td>
                            <td className="table-cell text-red-700 font-medium">Difference</td>
                            <td colSpan={translationResult ? 6 : isAdjusted ? 5 : 3} className="table-cell text-right font-mono text-sm text-red-700 font-semibold">
                              {formatCurrency(Math.abs(difference))}
                            </td>
                            <td className="table-cell" colSpan={1}></td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </div>

                  {/* Enhanced toolbar */}
                  <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="font-medium text-gray-700">{sortedFiltered.length} of {totalCount} accounts</span>
                      <span className="text-gray-300">|</span>
                      <span>Dr: <span className="font-mono font-medium text-gray-700">{formatCurrency(totalDebits)}</span></span>
                      <span>Cr: <span className="font-mono font-medium text-gray-700">{formatCurrency(totalCredits)}</span></span>
                      {!isBalanced && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-red-600 font-medium">Diff: {formatCurrency(Math.abs(difference))}</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 border-r border-gray-200 pr-3 mr-1">
                        <button
                          onClick={async () => {
                            if (!selectedEntity || !selectedPeriod || !toCurrency) return;
                            setIsTranslating(true);
                            setTranslationResult(null);
                            try {
                              const result = await trialBalancesApi.translate({
                                entity_id: selectedEntity,
                                period_id: selectedPeriod,
                                to_currency: toCurrency,
                              });
                              setTranslationResult(result);
                            } catch (err: any) {
                              addNotification({ type: 'error', title: 'Translation Failed', message: err?.detail || 'Could not translate.' });
                            } finally {
                              setIsTranslating(false);
                            }
                          }}
                          disabled={isTranslating || !toCurrency}
                          className="flex items-center gap-1.5 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-2.5 py-1 rounded font-medium disabled:opacity-50 transition-colors"
                        >
                          {isTranslating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
                          Translate
                        </button>
                        {translationResult && (
                          <div className="flex items-center gap-1">
                            {/* 
                            <button
                              onClick={handleCalculateNci}
                              disabled={isCalculatingNci}
                              className="flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-2.5 py-1 rounded font-medium disabled:opacity-50 transition-colors"
                              title="Calculate and post Minority Interest (NCI) for this entity"
                            >
                              {isCalculatingNci ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                              Calculate NCI
                            </button>
                            */}
                            <button
                              onClick={() => { setToCurrency(''); setTranslationResult(null); }}
                              className="flex items-center justify-center p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              title="Clear translation"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={loadData}
                        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Refresh
                      </button>
                      <button
                        onClick={handleExport}
                        disabled={isExporting || trialBalances.length === 0}
                        className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        {isExporting ? 'Exporting...' : 'Export XLSX'}
                      </button>
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Showing {((currentPage - 1) * PAGE_SIZE) + 1}&ndash;{Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-3 py-1 text-gray-700 font-medium">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setCurrentPage(totalPages)}
                          disabled={currentPage >= totalPages}
                          className="px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : null}

          {/* Statement Panel */}
          {activeStatement && (
            <div className="card overflow-hidden">
              {/* Panel header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {activeStatement === 'balance_sheet'
                    ? <BarChart3 className="w-4 h-4 text-primary-500" />
                    : <TrendingUp className="w-4 h-4 text-primary-500" />}
                  <span className="text-sm font-semibold text-gray-800">
                    {activeStatement === 'balance_sheet' ? 'Balance Sheet' : 'Income Statement'}
                  </span>
                  {statementData && (
                    <span className="badge bg-gray-100 text-gray-600">{statementData.currency}</span>
                  )}
                  {statementData?.is_balanced !== undefined && (
                    statementData.is_balanced
                      ? <span className="flex items-center gap-1 text-xs font-medium text-green-600"><CheckCircle2 className="w-3.5 h-3.5" /> Balanced</span>
                      : <span className="flex items-center gap-1 text-xs font-medium text-red-600"><AlertTriangle className="w-3.5 h-3.5" /> Out of Balance</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {/* Level selector */}
                  <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                    {([2, 3, 'all'] as StatementLevel[]).map((lvl) => (
                      <button
                        key={String(lvl)}
                        onClick={(e) => { e.stopPropagation(); setStatementLevel(lvl); }}
                        className={clsx(
                          'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
                          statementLevel === lvl
                            ? 'bg-white text-primary-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700',
                        )}
                      >
                        {lvl === 'all' ? 'All Levels' : `Level ${lvl}`}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleExportStatement}
                    disabled={isExporting || statementLoading || !statementData}
                    className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Export {statementLevel === 'all' ? '' : `L${statementLevel} `}Excel
                  </button>
                  <button onClick={() => { setActiveStatement(null); setStatementData(null); }} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              {statementLoading ? (
                <div className="p-8 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : statementData ? (
                <div className="px-4 pb-4 pt-0 overflow-auto max-h-[calc(100vh-250px)]">
                  {activeStatement === 'balance_sheet' ? (
                    <>
                      <StatementTable
                        currency={statementData.currency}
                        translatedCurrency={statementData.translated_currency}
                        sections={filterSectionsByLevel([
                          {
                            title: 'ASSETS', subsections: [
                              {
                                title: 'Non-current assets',
                                items: statementData.non_current_assets ?? [],
                                total: statementData.totals.non_current_assets,
                                totalLabel: 'Total Non-Current Assets',
                                translatedTotal: statementData.translated_totals?.non_current_assets,
                                ...getFirstRate(statementData.non_current_assets ?? [])
                              },
                              {
                                title: 'Current assets',
                                items: statementData.current_assets ?? [],
                                total: statementData.totals.current_assets,
                                totalLabel: 'Total Current Assets',
                                translatedTotal: statementData.translated_totals?.current_assets,
                                ...getFirstRate(statementData.current_assets ?? [])
                              },
                            ],
                            grandTotal: statementData.totals.total_assets,
                            grandTotalLabel: 'Total Assets',
                            translatedGrandTotal: statementData.translated_totals?.total_assets,
                            ...getFirstRate([
                              ...(statementData.non_current_assets ?? []),
                              ...(statementData.current_assets ?? [])
                            ])
                          },
                          {
                            title: 'EQUITY AND LIABILITIES', subsections: [
                              {
                                title: 'Equity',
                                items: statementData.equity ?? [],
                                total: statementData.totals.equity,
                                totalLabel: 'Total Equity',
                                translatedTotal: statementData.translated_totals?.equity,
                                ...getFirstRate(statementData.equity ?? [])
                              },
                              {
                                title: 'Non-current liabilities',
                                items: statementData.non_current_liabilities ?? [],
                                total: statementData.totals.non_current_liabilities,
                                totalLabel: 'Total Non-Current Liabilities',
                                translatedTotal: statementData.translated_totals?.non_current_liabilities,
                                ...getFirstRate(statementData.non_current_liabilities ?? [])
                              },
                              {
                                title: 'Current liabilities',
                                items: statementData.current_liabilities ?? [],
                                total: statementData.totals.current_liabilities,
                                totalLabel: 'Total Current Liabilities',
                                translatedTotal: statementData.translated_totals?.current_liabilities,
                                ...getFirstRate(statementData.current_liabilities ?? [])
                              },
                            ],
                            grandTotal: (statementData.totals.equity ?? 0) + (statementData.totals.non_current_liabilities ?? 0) + (statementData.totals.current_liabilities ?? 0),
                            grandTotalLabel: 'Total Equity and Liabilities',
                            translatedGrandTotal: statementData.translated_totals ? (statementData.translated_totals.equity ?? 0) + (statementData.translated_totals.non_current_liabilities ?? 0) + (statementData.translated_totals.current_liabilities ?? 0) : undefined,
                            ...getFirstRate([
                              ...(statementData.equity ?? []),
                              ...(statementData.non_current_liabilities ?? []),
                              ...(statementData.current_liabilities ?? [])
                            ])
                          },
                        ], statementLevel)}
                      />
                      {!statementData.is_balanced && (
                        <ImbalanceAnalysis data={statementData} />
                      )}
                    </>
                  ) : (() => {
                    // Build IS sub-sections from 2nd-level fs_line_item grouping
                    const revSections = statementData.revenue_sections ?? [{ label: 'Revenue', items: statementData.revenue ?? [], total: statementData.totals.total_revenue }];
                    const expSections = statementData.expense_sections ?? [{ label: 'Operating Expenses', items: statementData.expenses ?? [], total: statementData.totals.total_expenses }];
                    return (
                      <StatementTable
                        currency={statementData.currency}
                        translatedCurrency={statementData.translated_currency}
                        sections={filterSectionsByLevel([
                          {
                            title: 'REVENUE',
                            subsections: [
                              ...revSections.map((s) => {
                                const trTotal = s.items.reduce((acc, item) => acc + (item.translated_balance ?? 0), 0);
                                return {
                                  title: s.label,
                                  items: s.items,
                                  total: s.total,
                                  totalLabel: `Total ${s.label}`,
                                  translatedTotal: statementData.translated_currency ? trTotal : undefined,
                                  ...getFirstRate(s.items)
                                };
                              }),
                              // Section-level subtotal row
                              {
                                title: '', items: [],
                                total: statementData.totals.total_revenue, totalLabel: 'Total Revenue',
                                translatedTotal: statementData.translated_totals?.total_revenue,
                                ...getFirstRate(statementData.revenue ?? [])
                              },
                            ],
                            grandTotal: null,
                            grandTotalLabel: '',
                            ...getFirstRate(statementData.revenue ?? [])
                          },
                          {
                            title: 'EXPENSES',
                            subsections: [
                              ...expSections.map((s) => {
                                const trTotal = s.items.reduce((acc, item) => acc + (item.translated_balance ?? 0), 0);
                                return {
                                  title: s.label,
                                  items: s.items,
                                  total: s.total,
                                  totalLabel: `Total ${s.label}`,
                                  translatedTotal: statementData.translated_currency ? trTotal : undefined,
                                  ...getFirstRate(s.items)
                                };
                              }),
                              // Section-level subtotal row before NET INCOME grand total
                              {
                                title: '', items: [],
                                total: statementData.totals.total_expenses, totalLabel: 'Total Expenses',
                                translatedTotal: statementData.translated_totals?.total_expenses,
                                ...getFirstRate(statementData.expenses ?? [])
                              },
                            ],
                            grandTotal: statementData.totals.net_income,
                            grandTotalLabel: 'NET INCOME / (LOSS)',
                            translatedGrandTotal: statementData.translated_totals?.net_income,
                            ...getFirstRate([
                              ...(statementData.revenue ?? []),
                              ...(statementData.expenses ?? [])
                            ])
                          },
                        ], statementLevel)}
                      />
                    );
                  })()}
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Upload Trial Balance</h2>
              <button onClick={() => { setShowUpload(false); setSelectedFile(null); setUploadResult(null); setTbAiSuggestion(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Upload result */}
              {uploadResult && (
                <div className={clsx('px-4 py-3 rounded-lg border', uploadResult.errors.length > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200')}>
                  <p className={clsx('text-sm font-medium', uploadResult.errors.length > 0 ? 'text-amber-800' : 'text-green-800')}>
                    {uploadResult.rows_imported} rows imported successfully.
                  </p>
                  {uploadResult.errors.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {uploadResult.errors.map((err, i) => (
                        <li key={i} className="text-sm text-red-700">&bull; {err}</li>
                      ))}
                    </ul>
                  )}
                  {uploadResult.warnings.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {uploadResult.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-amber-700">&bull; {w}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* AI Suggestion for TB upload errors */}
              {uploadResult && uploadResult.errors.length > 0 && (
                <div className="space-y-2">
                  {!tbAiSuggestion ? (
                    <button
                      onClick={async () => {
                        setTbAiLoading(true);
                        try {
                          const res = await aiSuggestionsApi.getUploadSuggestion({
                            upload_type: 'trial_balance',
                            error_message: uploadResult.errors.join('; '),
                            warnings: uploadResult.warnings,
                            file_name: selectedFile?.name || undefined,
                          });
                          setTbAiSuggestion(res.suggestion);
                        } catch {
                          setTbAiSuggestion('AI suggestion unavailable. Configure AI in Settings → AI / LLM.');
                        } finally {
                          setTbAiLoading(false);
                        }
                      }}
                      disabled={tbAiLoading}
                      className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                    >
                      {tbAiLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing with AI...</>
                      ) : (
                        <><Sparkles className="w-4 h-4" /> Get AI Suggestion</>
                      )}
                    </button>
                  ) : (
                    <div className="px-4 py-3 rounded-lg border border-purple-200 bg-purple-50">
                      <div className="flex items-start gap-2">
                        <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-purple-800 mb-1">AI Suggestion</p>
                          <p className="text-sm text-purple-700">{tbAiSuggestion}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Entity & Period selection */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Entity</label>
                  <SearchableSelect
                    options={entities.map((e) => ({ value: String(e.id), label: e.name, secondary: e.code }))}
                    value={selectedEntity}
                    onChange={setSelectedEntity}
                    placeholder="Select Entity"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Period</label>
                  <select className="select-field" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                    <option value="">Select Period</option>
                    {(periods.filter((p) => p.status === 'open').length > 0
                      ? periods.filter((p) => p.status === 'open')
                      : periods
                    ).map((p) => (
                      <option key={p.id} value={String(p.id)}>{periodDisplayName(p)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Scenario & Balance Type */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Scenario</label>
                  <select className="select-field" value={uploadScenario} onChange={(e) => setUploadScenario(e.target.value)}>
                    <option value="Actual">Actual</option>
                    <option value="Budget">Budget</option>
                    <option value="Forecast">Forecast</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Balance Type</label>
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setUploadBalanceType('ytd')}
                      className={clsx(
                        'flex-1 py-2 text-sm font-medium transition-colors',
                        uploadBalanceType === 'ytd'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50',
                      )}
                    >
                      YTD
                    </button>
                    <button
                      type="button"
                      onClick={() => setUploadBalanceType('monthly')}
                      className={clsx(
                        'flex-1 py-2 text-sm font-medium transition-colors border-l border-gray-200',
                        uploadBalanceType === 'monthly'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50',
                      )}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
              </div>

              {/* Drop zone */}
              <div
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer',
                  dragOver ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <File className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Drag and drop your file here, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Supports Excel (.xlsx, .xls) and CSV files</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => { setShowUpload(false); setSelectedFile(null); setUploadResult(null); setTbAiSuggestion(null); }} className="btn-secondary">Cancel</button>
              <button onClick={handleUpload} disabled={uploading || !selectedFile} className="btn-primary">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opening Balances Upload Modal */}
      {showOBUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Upload Opening Balances</h2>
              <button onClick={() => { setShowOBUpload(false); setObFile(null); setObResult(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Upload prior-year closing balances (FY opening balances) for the selected entity and period.
                The file should contain <strong>account_code</strong> and <strong>balance</strong> columns
                with group account codes and their net balances (positive = debit, negative = credit).
              </p>

              {!selectedEntity && (
                <p className="text-sm text-amber-600 font-medium">Please select an entity first.</p>
              )}
              {!selectedPeriod && (
                <p className="text-sm text-amber-600 font-medium">Please select a period first.</p>
              )}

              {obResult && (
                <div className={`p-3 rounded-lg text-sm ${obResult.errors.length > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  <p className="font-medium">{obResult.rows_imported} opening balance(s) imported.</p>
                  {obResult.warnings.length > 0 && (
                    <ul className="mt-1 list-disc list-inside text-xs text-amber-700">
                      {obResult.warnings.slice(0, 5).map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  )}
                  {obResult.errors.length > 0 && (
                    <ul className="mt-1 list-disc list-inside text-xs">
                      {obResult.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                </div>
              )}

              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${obFile ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-primary-400'}`}
                onClick={() => obFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={obFileInputRef}
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => { setObFile(e.target.files?.[0] || null); setObResult(null); }}
                />
                {obFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <FileSpreadsheet className="w-5 h-5" />
                    <span className="text-sm font-medium">{obFile.name}</span>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Click to select your opening balance file
                    </p>
                    <p className="text-xs text-gray-400">Excel (.xlsx) or CSV with account_code + balance columns</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => { setShowOBUpload(false); setObFile(null); setObResult(null); }} className="btn-secondary">Cancel</button>
              <button
                disabled={obUploading || !obFile || !selectedEntity || !selectedPeriod}
                className="btn-primary"
                onClick={async () => {
                  if (!obFile || !selectedEntity || !selectedPeriod) return;
                  setObUploading(true);
                  try {
                    const result = await trialBalancesApi.uploadOpeningBalances(selectedEntity, selectedPeriod, obFile);
                    setObResult(result);
                  } catch (err: any) {
                    const apiErr = err.response?.data;
                    setObResult({ rows_imported: 0, errors: [apiErr?.detail || 'Upload failed'], warnings: [] });
                  } finally {
                    setObUploading(false);
                  }
                }}
              >
                {obUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload Opening Balances</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Trial Balances</h2>
              <button onClick={() => { setShowBulkUpload(false); setBulkFiles([]); setBulkResult(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-600">
                Select multiple monthly TB files. The month is <strong>auto-detected from filename</strong> (e.g. <code>Apr.csv</code>, <code>Jun.csv</code>, <code>March_2025.xlsx</code>) and matched to the correct period.
              </p>

              {!selectedEntity && <p className="text-sm text-amber-600 font-medium">Please select an entity first.</p>}

              {bulkResult && (
                <div className={`p-4 rounded-lg text-sm ${bulkResult.files_errored > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className="font-semibold text-gray-800">
                    {bulkResult.files_processed} of {bulkResult.total_files} files processed — {bulkResult.total_rows_imported} total rows imported
                    {bulkResult.files_skipped > 0 && <span className="text-amber-600"> — {bulkResult.files_skipped} skipped</span>}
                    {bulkResult.files_errored > 0 && <span className="text-red-600"> — {bulkResult.files_errored} errors</span>}
                  </p>
                  {bulkResult.file_results && (
                    <div className="mt-2 space-y-1">
                      {bulkResult.file_results.map((fr: any, i: number) => (
                        <div key={i} className={`flex items-center gap-2 text-xs ${fr.status === 'success' ? 'text-green-700' : fr.status === 'skipped' ? 'text-amber-700' : 'text-red-700'}`}>
                          {fr.status === 'success' ? <CheckCircle2 className="w-3.5 h-3.5" /> : fr.status === 'skipped' ? <AlertTriangle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                          <span className="font-medium">{fr.filename}</span>
                          {fr.period && <span className="text-gray-500">→ {fr.period}</span>}
                          <span>{fr.rows_imported} rows</span>
                          {fr.reason && <span className="text-gray-500">({fr.reason})</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${bulkFiles.length > 0 ? 'border-green-400 bg-green-50' : 'border-gray-200 hover:border-primary-400'}`}
                onClick={() => bulkFileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={bulkFileInputRef}
                  className="hidden"
                  accept=".xlsx,.xls,.csv"
                  multiple
                  onChange={(e) => { setBulkFiles(Array.from(e.target.files || [])); setBulkResult(null); }}
                />
                {bulkFiles.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-green-700">{bulkFiles.length} file(s) selected:</p>
                    <div className="flex flex-wrap gap-2 justify-center">
                      {bulkFiles.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-green-300 rounded text-xs text-green-800">
                          <FileSpreadsheet className="w-3 h-3" /> {f.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <Layers className="w-8 h-8 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-600 mb-1">
                      Click to select multiple TB files, or <span className="text-primary-600 font-medium">browse</span>
                    </p>
                    <p className="text-xs text-gray-400">Filenames like Apr.csv, Jun.csv, Dec.xlsx — month is auto-detected</p>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => { setShowBulkUpload(false); setBulkFiles([]); setBulkResult(null); }} className="btn-secondary">Cancel</button>
              <button
                disabled={bulkUploading || bulkFiles.length === 0 || !selectedEntity}
                className="btn-primary"
                onClick={async () => {
                  if (bulkFiles.length === 0 || !selectedEntity) return;
                  setBulkUploading(true);
                  setBulkResult(null);
                  try {
                    const year = periods.length > 0 ? periods[0].year : new Date().getFullYear();
                    const result = await trialBalancesApi.uploadBulk(selectedEntity, year, bulkFiles, 'Actual', 'monthly');
                    setBulkResult(result);
                    if (result.files_processed > 0) {
                      await loadData();
                      if (viewMode === 'uploads') await loadUploads();
                    }
                  } catch (err: any) {
                    const msg = err.response?.data?.detail || 'Bulk upload failed';
                    setBulkResult({ total_files: bulkFiles.length, files_processed: 0, files_skipped: 0, files_errored: bulkFiles.length, total_rows_imported: 0, file_results: [], errors: [msg], warnings: [] });
                  } finally {
                    setBulkUploading(false);
                  }
                }}
              >
                {bulkUploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading {bulkFiles.length} files...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />Upload {bulkFiles.length || ''} Files</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Export Options Modal ───────────────────────────────────────── */}
      {showExportModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setShowExportModal(false)}
        >
          <div
            className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-8 space-y-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Export Options</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Choose what data to include in the Excel export.
              </p>
            </div>

            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  id="export-original"
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600"
                  checked={exportOriginal}
                  onChange={(e) => setExportOriginal(e.target.checked)}
                />
                <div>
                  <span className="block font-medium text-gray-800 dark:text-gray-200">
                    Original Data (Base Currency)
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    Debit, Credit, and Net Balance in the entity's functional currency.
                  </span>
                </div>
              </label>

              <label className={`flex items-start gap-3 ${translationResult ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                <input
                  id="export-translated"
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600"
                  checked={exportTranslated}
                  disabled={!translationResult}
                  onChange={(e) => {
                    setExportTranslated(e.target.checked);
                    if (!e.target.checked) setExportIncludeCta(false);
                  }}
                />
                <div>
                  <span className="block font-medium text-gray-800 dark:text-gray-200">
                    Translated Data ({toCurrency || 'Target Currency'})
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    {translationResult
                      ? 'Includes translated Debit, Credit, and FX Rate per row.'
                      : 'Run the inline translation first to enable this option.'}
                  </span>
                </div>
              </label>
              {exportContext === 'trial_balance' && translationResult && (
                <div className="pl-7 mt-2 space-y-2 border-l-2 border-blue-100 dark:border-blue-900/30 animate-in slide-in-from-left-2 duration-300">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      id="export-include-cta"
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 accent-blue-600"
                      checked={exportIncludeCta}
                      onChange={(e) => {
                        setExportIncludeCta(e.target.checked);
                        if (e.target.checked) setExportTranslated(true);
                      }}
                    />
                    <div>
                      <span className="block font-medium text-gray-800 dark:text-gray-200">
                        Include CTA Adjustment
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        Add a balancing row for Currency Translation Adjustment.
                      </span>
                    </div>
                  </label>
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700" />

            <div className="flex justify-end gap-3">
              <button
                id="export-cancel-btn"
                onClick={() => setShowExportModal(false)}
                className="px-5 py-2.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                id="export-submit-btn"
                disabled={!exportOriginal && !exportTranslated}
                onClick={submitExport}
                className="px-5 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
